import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const GAME_LABELS: Record<string, string> = {
  tavli: "Τάβλι",
  portes: "Πόρτες",
  plakoto: "Πλακωτό",
  fevga: "Φεύγα",
  stratego: "Imperium 1800",
  ludo: "Γκρινιάρη",
  kremala: "Κρεμάλα",
  agonia: "Αγωνία",
  xeri: "Ξερή",
};

const TAVLI_GAME_TYPES = ["tavli", "portes", "plakoto", "fevga"];

async function attachRelevantStats<T extends Record<string, any>>(
  winner: T,
  gameType: string | null,
  winnerKind: string | null,
) {
  if (!gameType || !TAVLI_GAME_TYPES.includes(gameType) || winnerKind === "npc") {
    return { ...winner, stat_scope: "all" };
  }

  const userId = winner.user_id as string | undefined;
  if (!userId) return { ...winner, stat_scope: "all" };

  const { data } = await supabase
    .from("game_stats")
    .select("total_wins, total_games")
    .eq("user_id", userId)
    .in("game_type", TAVLI_GAME_TYPES);

  if (!data?.length) return { ...winner, stat_scope: "all" };

  return {
    ...winner,
    total_wins: data.reduce((sum, row) => sum + (row.total_wins || 0), 0),
    total_games: data.reduce((sum, row) => sum + (row.total_games || 0), 0),
    stat_scope: "tavli",
  };
}

/**
 * Ελαφρύ, ΑΝΕΞΑΡΤΗΤΟ query μόνο για την πληροφορία «Πρόσφατα νίκησε».
 * Δεν αγγίζει καθόλου το index-boot cache (leaderboard/spotlight pools).
 */
export function useRecentWinner() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const channel = supabase
      .channel("recent-winner-events")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "platform_events" },
        (payload) => {
          const eventType = (payload.new as { event_type?: string }).event_type;
          if (eventType === "game_win" || eventType === "game_loss") {
            queryClient.invalidateQueries({ queryKey: ["recent-winner"] });
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  return useQuery({
    queryKey: ["recent-winner"],
    queryFn: async () => {
      const { data: events } = await supabase
        .from("platform_events")
        .select("event_type, user_id, user_name, message, created_at, winner_ref, winner_kind, game_type")
        .in("event_type", ["game_win", "game_loss"])
        .order("created_at", { ascending: false })
        .limit(20);

      for (const e of events || []) {
        if (e.winner_ref && e.winner_kind) {
          const winnerQuery = e.winner_kind === "npc"
            ? supabase
                .from("npc_profiles")
                .select("id, display_name, username, total_wins, total_games, level")
                .eq("id", e.winner_ref)
                .maybeSingle()
            : supabase
                .from("profiles")
                .select("user_id, display_name, username, avatar_url, total_wins, total_games, level")
                .eq("user_id", e.winner_ref)
                .eq("is_guest", false)
                .maybeSingle();
          const { data: structuredWinner } = await winnerQuery;
          if (structuredWinner) {
            const winnerWithStats = await attachRelevantStats(
              structuredWinner,
              e.game_type,
              e.winner_kind,
            );
            return {
              ...winnerWithStats,
              game: GAME_LABELS[e.game_type || ""] || e.game_type || null,
              finished_at: e.created_at,
            };
          }
        }

        // Compatibility for events written before stable winner fields existed.
        const lossWinnerMatch = e.event_type === "game_loss"
          ? /έχασε από τ(?:ον|ην)\s+(.+?)\s+στο\s+/u.exec(e.message || "")
          : null;
        const winnerName = lossWinnerMatch?.[1]?.trim();

        if (e.event_type === "game_loss" && !winnerName) continue;
        if (e.event_type === "game_win" && !e.user_id) continue;

        const [prof, npc] = e.event_type === "game_win"
          ? await Promise.all([
              supabase
                .from("profiles")
                .select("user_id, display_name, username, avatar_url, total_wins, total_games, level")
                .eq("user_id", e.user_id)
                .eq("is_guest", false)
                .maybeSingle(),
              supabase
                .from("npc_profiles")
                .select("id, display_name, username, total_wins, total_games, level")
                .eq("id", e.user_id)
                .maybeSingle(),
            ])
          : await Promise.all([
              supabase
                .from("profiles")
                .select("user_id, display_name, username, avatar_url, total_wins, total_games, level")
                .ilike("display_name", winnerName || "")
                .eq("is_guest", false)
                .maybeSingle(),
              supabase
                .from("npc_profiles")
                .select("id, display_name, username, total_wins, total_games, level")
                .ilike("display_name", winnerName || "")
                .maybeSingle(),
            ]);

        const found: any = prof.data || npc.data;
        if (!found) continue;

        const gMatch = /στο\s+(.+?)(?:!\s*🏆)?\s*$/u.exec(e.message || "");
        const winnerKind = prof.data ? "profile" : "npc";
        const foundWithStats = await attachRelevantStats(found, e.game_type, winnerKind);
        return { ...foundWithStats, game: gMatch?.[1]?.trim() || null, finished_at: e.created_at };
      }

      return null;
    },
    staleTime: 60 * 1000,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
}
